import { IconButton } from "@hortis/ui/button";
import { Dialog } from "@hortis/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { HoverCard, HoverCardTrigger } from "@hortis/ui/hover-card";
import {
  CameraPlus,
  DotsHorizontal,
  ExternalLink1,
  Eye,
  EyeOff,
  GitBranch1,
  MarkerPin4,
  RefreshCw5,
  Trash3,
} from "@hortis/ui/icons";
import { IUCNRedListTag } from "@hortis/ui/iucn-red-list-tag";
import { Tag } from "@hortis/ui/tag";
import { Link, useParams } from "@tanstack/react-router";
import {
  type MaterialFieldsFragment,
  MaterialGroup,
  PlantMaterialStatus,
} from "generated/graphql";
import { useState } from "react";
import { AccessionMaterialStatusSummary } from "src/components/accession-material-status-summary/accession-material-status-summary";
import { TrashMaterialDialogContent } from "src/components/dialog/variants/trash-material";
import { AccessionHoverCardContent } from "src/components/rich-text-editor/mention/accession-hover-card-content";
import { TaxonHoverCardContent } from "src/components/rich-text-editor/mention/taxon-hover-card-content";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";
import { RestoreMaterialDialog } from "src/features/collection/components/plant-materials/restore-material-dialog";
import { MaterialEditStage } from "src/features/records/material/components/edit-drawer/types";
import { useCollectionSiteSettings } from "src/utils/hooks/collection-site-from-url";
import {
  RecordPageHeader,
  RecordPageHeaderContent,
  RecordPageHeaderReference,
  RecordPageHeaderReferenceButton,
  RecordPageHeaderReferenceLabel,
  RecordPageHeaderTitle,
} from "../../../components/record-page-header";
import { CreatePhotosDialog } from "../create-photos-dialog/create-photos-dialog";
import { MakePrivateDialog } from "./make-private-dialog";
import { MakePublicDialog } from "./make-public-dialog";
import { MaterialCopyDropdownMenuSub } from "./material-copy-dropdown-menu-sub";

type MaterialRecordPageHeaderProps = {
  material: MaterialFieldsFragment;
  refetchMaterial: () => void;
  requestEdit: (editStage?: MaterialEditStage) => void;
  canEdit: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function MaterialRecordPageHeader({
  material,
  refetchMaterial,
  canEdit,
}: MaterialRecordPageHeaderProps) {
  const isPublic = material.public;
  const { isPublic: isSitePublic } = useCollectionSiteSettings();
  const { siteSlug } = useParams({
    from: "/_layout/site/$siteSlug/material/$materialNumber",
  });

  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [makePublicDialogOpen, setMakePublicDialogOpen] = useState(false);
  const [makePrivateDialogOpen, setMakePrivateDialogOpen] = useState(false);
  const [createPhotosDialogOpen, setCreatePhotosDialogOpen] = useState(false);

  const isPresentPlant =
    material.status === PlantMaterialStatus.Present &&
    material.materialGroup === MaterialGroup.Plant;

  return (
    <RecordPageHeader>
      <RecordPageHeaderContent>
        <RecordPageHeaderTitle>
          {createMaterialNumber(
            material.accession?.accessionNumber ?? "",
            material.qualifier,
            "/",
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                ariaLabel="More actions"
                data-cy="action-menu-button"
                variant="tertiaryGray"
                icon={DotsHorizontal}
                size="sm"
                className="p-1.5"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {canEdit && (
                <>
                  <DropdownMenuItem
                    data-cy="add-photos-record-menu-item"
                    onSelect={() => {
                      setCreatePhotosDialogOpen(true);
                    }}
                  >
                    <CameraPlus className="h-4 w-4 text-grey-500" />
                    Add photos
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {material.position != null && material.accession != null && (
                <>
                  <DropdownMenuItem asChild data-cy="show-on-map-menu-item">
                    <Link
                      to="/site/$siteSlug/map/material/$materialNumber"
                      from="/site/$siteSlug"
                      params={{
                        materialNumber: createMaterialNumber(
                          material.accession.accessionNumber,
                          material.qualifier,
                        ),
                      }}
                      search={{
                        latLng: {
                          lng: material.position.longitude,
                          lat: material.position.latitude,
                        },
                        zoom: 19,
                      }}
                    >
                      <MarkerPin4 className="h-4 w-4 text-grey-500" />
                      Open on map
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {isPublic && isPresentPlant && isSitePublic && (
                <>
                  <DropdownMenuItem
                    data-cy="open-public-record-menu-item"
                    asChild
                  >
                    <a
                      target="_blank"
                      href={`/public/site/${siteSlug}/map/plant/${material.id}`}
                    >
                      <ExternalLink1 className="h-4 w-4 text-grey-500" />
                      Open public record
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <MaterialCopyDropdownMenuSub material={material} />
              {canEdit && (
                <>
                  {isPresentPlant && !material.archived && isSitePublic && (
                    <>
                      {isPublic && (
                        <DropdownMenuItem
                          data-cy="make-private-menu-item"
                          onClick={() => {
                            setMakePrivateDialogOpen(true);
                          }}
                        >
                          <EyeOff className="h-4 w-4 text-grey-500" />
                          Make private
                        </DropdownMenuItem>
                      )}
                      {!isPublic && (
                        <DropdownMenuItem
                          data-cy="make-public-menu-item"
                          onClick={() => {
                            setMakePublicDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 text-grey-500" />
                          Make public
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {!material.archived && (
                    <DropdownMenuItem
                      onClick={() => {
                        setTrashDialogOpen(true);
                      }}
                      testId="archive-material-menu-item"
                    >
                      <Trash3 className="h-4 w-4 text-grey-500" />
                      Delete
                    </DropdownMenuItem>
                  )}
                  {material.archived && (
                    <DropdownMenuItem
                      onClick={() => {
                        setRestoreDialogOpen(true);
                      }}
                      testId="restore-material-menu-item"
                    >
                      <RefreshCw5 className="h-4 w-4 text-grey-500" />
                      Restore
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </RecordPageHeaderTitle>
        <div className="pt-2">
          {material.accession?.taxon != null && (
            <RecordPageHeaderReference>
              <RecordPageHeaderReferenceLabel>
                Taxon
              </RecordPageHeaderReferenceLabel>
              <HoverCard>
                <HoverCardTrigger asChild>
                  <RecordPageHeaderReferenceButton asChild>
                    <Link
                      to="/taxon/$taxonId"
                      params={{ taxonId: material.accession.taxon.id }}
                    >
                      {material.accession?.taxon.sharedScientificName?.taxon
                        ?.globalRedListStatus != null &&
                      material.accession.taxon.fullyValidated === true ? (
                        <IUCNRedListTag
                          status={
                            material.accession?.taxon.sharedScientificName
                              ?.taxon?.globalRedListStatus
                          }
                          short
                          className="text-xs py-px px-1 rounded-md"
                        />
                      ) : (
                        <GitBranch1 className="h-4 w-4 text-grey-500" />
                      )}
                      {material.accession?.scientificName}
                      <span className="text-grey-500">
                        {material.accession?.taxon?.commonName}
                      </span>
                    </Link>
                  </RecordPageHeaderReferenceButton>
                </HoverCardTrigger>
                <TaxonHoverCardContent taxon={material.accession.taxon} />
              </HoverCard>
            </RecordPageHeaderReference>
          )}
          {material.accession != null && (
            <RecordPageHeaderReference>
              <RecordPageHeaderReferenceLabel>
                Accession
              </RecordPageHeaderReferenceLabel>
              <HoverCard>
                <HoverCardTrigger asChild>
                  <RecordPageHeaderReferenceButton asChild>
                    <Link
                      to="/accession/$id"
                      params={{ id: material.accession.id }}
                    >
                      <Tag className="border-grey-100 bg-grey-100 text-xs py-px px-1 rounded-md">
                        {material.accession.provenanceCategory?.codeId}
                      </Tag>
                      {/* <Folder className="h-4 w-4 text-grey-500" /> */}
                      {material.accession.accessionNumber}
                      <AccessionMaterialStatusSummary
                        materials={material.accession?.plantMaterial ?? []}
                      />
                    </Link>
                  </RecordPageHeaderReferenceButton>
                </HoverCardTrigger>
                <AccessionHoverCardContent accession={material.accession} />
              </HoverCard>
            </RecordPageHeaderReference>
          )}
        </div>
      </RecordPageHeaderContent>
      {canEdit && (
        <>
          {
            <>
              <Dialog open={trashDialogOpen} onOpenChange={setTrashDialogOpen}>
                <TrashMaterialDialogContent
                  id={material.id}
                  onSuccess={() => {
                    setTrashDialogOpen(false);
                    refetchMaterial();
                  }}
                />
              </Dialog>
              <RestoreMaterialDialog
                open={restoreDialogOpen}
                onClose={() => {
                  refetchMaterial();
                  setRestoreDialogOpen(false);
                }}
                materialId={material.id}
              />

              <MakePrivateDialog
                open={makePrivateDialogOpen}
                onOpenChange={setMakePrivateDialogOpen}
                id={material.id}
                onSuccess={refetchMaterial}
              />
              <MakePublicDialog
                open={makePublicDialogOpen}
                onOpenChange={setMakePublicDialogOpen}
                id={material.id}
                onSuccess={refetchMaterial}
              />
              <CreatePhotosDialog
                materialId={material.id}
                open={createPhotosDialogOpen}
                onOpenChange={setCreatePhotosDialogOpen}
              />
            </>
          }
        </>
      )}
    </RecordPageHeader>
  );
}

export default MaterialRecordPageHeader;
